import React, { useEffect, useContext, useRef } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import { basePageWrap } from '../BasePage';

import ScrolledVideoHero from '../../components/ScrolledVideoHero';
import InsightsSectionHeading from '../../components/InsightsSectionHeading/InsightsSectionHeading';
import BigNumbersBlock from '../../components/BigNumbersBlock/BigNumbersBlock';
import InsightsFullWidthVideo from '../../components/InsightsFullWidthVideo/InsightsFullWidthVideo';
import BigNumberVideoBlock from '../../components/BigNumberVideoBlock/BigNumberVideoBlock';
import PieBarChart3d from '../../components/PieBarChart3d/PieBarChart3d';
import BarChart3d from '../../components/BarChart3d/BarChart3d';
import GravityBallStats from '../../components/GravityBallStats/GravityBallStats';
import InsightsContactCta from '../../components/InsightsContactCta/InsightsContactCta';
import BarChartHorizontal from '../../components/BarChartHorizontal/BarChartHorizontal';
import Footer from '../../components/Footer';

import useIntersectionObserver from '../../hooks/useIntersectionObserver';

import {
  BackgroundContext,
  AnimationsActiveContext,
} from '../../containers/BasePage';
import ThreeJsMultiplexedCanvasProvider from '../../components/ThreeJsMultiplexedCanvasProvider/ThreeJsMultiplexedCanvasProvider';

import s from './InsightsPage.module.scss';

const parseStreamField = (field) => field?.[0]?.value || {};

const InsightsSection = (props) => {
  const {
    bgColor = 'dark',
    className,
    children,
    threshold,
    rootMargin,
  } = props;

  const [sectionRef, sectionVisible] = useIntersectionObserver(
    threshold,
    rootMargin,
    false
  );
  const { toggleWhiteBackground } = useContext(BackgroundContext);

  useEffect(() => {
    if (sectionVisible) {
      toggleWhiteBackground(bgColor == 'dark' ? false : true);
    }
  }, [sectionVisible]);

  return (
    <div className={className} ref={sectionRef}>
      {children}
    </div>
  );
};

InsightsSection.defaultProps = {
  bgColor: 'dark',
  threshold: 0.3,
  rootMargin: '',
};

InsightsSection.propTypes = {
  bgColor: PropTypes.oneOf(['dark', 'light']),
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  threshold: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  rootMargin: PropTypes.string,
};

const GradientWrap = ({
  children,
  gradient,
  layout,
  hidden = false,
  modifiers = [],
}) => {
  const gradientCls = classNames(
    s.GradientWrap,
    {
      [s.PinkBlue]: gradient === 'pink-blue',
      [s.PinkOrange]: gradient === 'pink-orange',
      [s.PinkPurple]: gradient === 'pink-purple',
      [s.GreenBlue]: gradient === 'green-blue',

      [s.LayoutUneven]: layout === 'uneven',
      [s.LayoutRightOnly]: layout === 'right-only',

      [s.Hidden]: hidden,
    },
    modifiers.map((m) => s[m])
  );

  return (
    <div className={gradientCls}>
      <div className={s.GradientLeft} />
      <div className={s.GradientRight} />
      {children}
    </div>
  );
};

GradientWrap.propTypes = {
  children: PropTypes.node,
  gradient: PropTypes.oneOf([
    'pink-blue',
    'pink-orange',
    'pink-purple',
    'green-blue',
  ]),
  layout: PropTypes.oneOf(['even', 'uneven', 'right-only']),
};

const ImageFullwidthWrap = ({ image, children, className }) => {
  const [imageRef, imageVisible] = useIntersectionObserver(
    0.1,
    '30% 0px 0px 0px'
  );

  const imageStyle = {
    objectFit: 'cover',
    zIndex: -1,
  };

  if (!!image && !!image.focal && !!image.focal.x && !!image.focal.y) {
    imageStyle.objectPosition = `${image.focal.x} ${image.focal.y}`;
  }

  const wrapCls = classNames(s.ImageFullwidthWrap, className, {
    [s.Visible]: imageVisible,
  });

  return (
    <div className={wrapCls}>
      {image && (
        <div className={s.ImageFullwidthWrapContainer}>
          <Image
            ref={imageRef}
            sizes="100vw"
            className={s.Image}
            src={image.url}
            alt={image?.alt}
            title={image?.title}
            fill
            style={imageStyle}
          />
        </div>
      )}
      {children}
    </div>
  );
};

const ReduceMotionButton = () => {
  const { t } = useTranslation();

  const { whiteBackground } = useContext(BackgroundContext);
  const { animationsActive, toggleAnimationsActive } = useContext(
    AnimationsActiveContext
  );

  const wrapperCls = classNames(s.ReduceMotionButtonWrapper, {
    [s.BlackText]: whiteBackground,
  });

  const iconSrc = `/img/${
    animationsActive
      ? whiteBackground
        ? 'icon-pause-black'
        : 'icon-pause-white'
      : whiteBackground
      ? 'icon-play-black'
      : 'icon-play-white'
  }.svg`;

  return (
    <div className={wrapperCls}>
      <button className={s.ReduceMotionButton} onClick={toggleAnimationsActive}>
        <Image
          className={s.ReduceMotionIcon}
          width={20}
          height={20}
          src={iconSrc}
          alt={animationsActive ? t('Generic.pause') : t('Generic.play')}
        />
        {animationsActive
          ? t('Insights.reduceMotion')
          : t('Insights.enableRegularMotion')}
      </button>
    </div>
  );
};

const InsightsPage = (props) => {
  const {
    hero = {},

    sectionOneIntro,
    sectionOneBigNumberStats,
    sectionOneBigNumberStatsAndVideo,

    sectionTwoIntro,
    sectionTwoPieChart,

    sectionThreeIntro,
    sectionThreeBarChart,

    sectionFourIntro,
    sectionFourGravityBalls,
    sectionFourContact,

    sectionFiveIntro,
    sectionFiveIntroImage,
    sectionFiveIntro2,
    sectionFiveBigNumberStats,

    sectionSixIntro,
    sectionSixBigNumberStats,

    sectionSevenIntro,
    sectionSevenBarChart,
    // sectionSevenOutroImage,

    sectionEightIntro,
    sectionEightBarChart,

    sectionNineIntro,
    sectionNineContact,

    siteSetting,
  } = props;

  const { whiteBackground } = useContext(BackgroundContext);
  const { animationsActive, setAnimationsActive } = useContext(
    AnimationsActiveContext
  );

  return (
    <ThreeJsMultiplexedCanvasProvider>
      <ReduceMotionButton />
      <div className={s.Root}>
        {hero && <ScrolledVideoHero {...hero} />}

        {/* section one  */}
        <InsightsSection className={s.SectionOne} bgColor="dark">
          <GradientWrap
            hidden={whiteBackground}
            gradient="pink-blue"
            modifiers={['RaiseRight', 'RaiseLeft']}>
            <InsightsSectionHeading
              {...parseStreamField(sectionOneIntro)}
              rightIcon="cloud"
              iconTheme="blue-purple"
            />
            <BigNumbersBlock
              {...parseStreamField(sectionOneBigNumberStats)}
              theme="pink-purple"
              textColor={whiteBackground ? 'text-dark' : 'text-light'}
            />
          </GradientWrap>
          <InsightsFullWidthVideo videoSlug="fiber-loop" />
          <GradientWrap
            hidden={whiteBackground}
            gradient="pink-blue"
            layout="uneven"
            modifiers={['LowerLeftResponsive', 'RaiseLeft--Desktop']}>
            <BigNumberVideoBlock
              textColor={whiteBackground ? 'text-dark' : 'text-light'}
              {...parseStreamField(sectionOneBigNumberStatsAndVideo)}
            />
          </GradientWrap>
        </InsightsSection>

        {/* section two */}
        <InsightsSection
          className={s.SectionTwo}
          bgColor="light"
          rootMargin="-5%">
          <InsightsSectionHeading
            {...parseStreamField(sectionTwoIntro)}
            titleTheme="pink-purple"
            textColor={whiteBackground ? 'text-dark' : 'text-light'}
          />
          <PieBarChart3d
            {...parseStreamField(sectionTwoPieChart)}
            textTheme={whiteBackground ? 'dark' : 'light'}
          />
          <InsightsFullWidthVideo
            className={s.SectionTwoVideo}
            videoSlug="besoksnaring-light-loop"
            loaderColor="dark"
          />
        </InsightsSection>

        {/* section three */}
        <InsightsSection className={s.SectionThree} bgColor="light">
          <InsightsSectionHeading
            {...parseStreamField(sectionThreeIntro)}
            titleTheme="pink-orange"
            textColor="text-dark"
            rightIcon="cloud"
          />
          <BarChart3d
            {...parseStreamField(sectionThreeBarChart)}
            textTheme="dark"
            theme="orange-pink"
          />
        </InsightsSection>

        {/* section four */}
        <InsightsSection
          className={s.SectionFour}
          bgColor="light"
          rootMargin={'-10%'}>
          <InsightsSectionHeading
            {...parseStreamField(sectionFourIntro)}
            titleTheme="blue-purple"
            textColor="text-dark"
            videoIcon="balloon"
          />
          <GravityBallStats {...parseStreamField(sectionFourGravityBalls)} />
          <InsightsContactCta
            {...parseStreamField(sectionFourContact)}
            theme="blue-purple"
          />
        </InsightsSection>

        {/* section five */}
        <InsightsSection
          className={s.SectionFive}
          bgColor="dark"
          threshold={[0.25, 0.25, 0.25, 0.25]}
          rootMargin={'90% 0% 0% 0%'}>
          <ImageFullwidthWrap
            className={s.ImageFullwidthWrapOverlay}
            image={sectionFiveIntroImage}>
            <InsightsSectionHeading
              {...parseStreamField(sectionFiveIntro)}
              modifiers={['DelayedInTransition']}
            />
          </ImageFullwidthWrap>

          <GradientWrap
            hidden={whiteBackground}
            gradient="pink-orange"
            layout="right-only"
            modifiers={['RaiseRight']}>
            <InsightsSectionHeading
              {...parseStreamField(sectionFiveIntro2)}
              iconTheme="pink-orange"
              leftIcon="clouds"
              rightIcon="cloud"
            />
          </GradientWrap>

          <BigNumbersBlock
            {...parseStreamField(sectionFiveBigNumberStats)}
            theme="pink-orange"
          />
          <InsightsFullWidthVideo
            className={s.SectionFiveVideo}
            videoSlug="befolkning-loop"
            largeQuery="(min-width: 1300px)"
            layout="extra-wide"
            loaderColor="light"
          />
        </InsightsSection>

        {/* section six */}
        <InsightsSection className={s.SectionSix} bgColor="dark">
          <InsightsSectionHeading {...parseStreamField(sectionSixIntro)} />
          <GradientWrap
            hidden={whiteBackground}
            gradient="pink-purple"
            layout="right-only"
            modifiers={['LowerRight']}>
            <BigNumbersBlock
              {...parseStreamField(sectionSixBigNumberStats)}
              theme="pink-purple"
            />
          </GradientWrap>
          <InsightsFullWidthVideo
            className={s.SectionSixVideo}
            videoSlug="foretagande-loop"
            largeQuery="(min-width: 1300px)"
            layout="extra-wide"
            loaderColor="light"
          />
        </InsightsSection>

        {/* section seven */}
        <InsightsSection className={s.SectionSeven} bgColor="dark">
          <GradientWrap hidden={whiteBackground} gradient="pink-purple">
            <InsightsSectionHeading
              {...parseStreamField(sectionSevenIntro)}
              rightIcon="cloud"
              iconTheme="blue-purple"
            />
          </GradientWrap>
          <BarChart3d
            {...parseStreamField(sectionSevenBarChart)}
            textTheme="light"
            theme="blue-purple"
          />
          {/* <ImageFullwidthWrap
            className={s.SectionSevenOutroImage}
            image={sectionSevenOutroImage}
          /> */}
        </InsightsSection>

        {/* section eight */}
        <InsightsSection className={s.SectionEight} bgColor="dark">
          <GradientWrap
            hidden={whiteBackground}
            gradient="pink-orange"
            layout="uneven">
            <InsightsSectionHeading {...parseStreamField(sectionEightIntro)} />
            <BarChartHorizontal {...parseStreamField(sectionEightBarChart)} />
          </GradientWrap>
        </InsightsSection>

        {/* section nine */}
        <InsightsSection className={s.SectionNine} bgColor="dark">
          <GradientWrap
            hidden={whiteBackground}
            gradient="green-blue"
            layout="uneven">
            <InsightsSectionHeading {...parseStreamField(sectionNineIntro)} />
            <InsightsContactCta
              {...parseStreamField(sectionNineContact)}
              theme="blue-green"
            />
          </GradientWrap>
        </InsightsSection>

        <div className={s.FooterWrapper}>
          <div className={`${s.FooterHr} grid`} />
          <Footer
            {...siteSetting?.footer}
            cookieConsent={siteSetting?.cookieConsent}
            gradient="BlueGreen"
            modifiers={['AllowOverflow', 'BlendScreen', 'MarginTop']}
          />
        </div>
      </div>
    </ThreeJsMultiplexedCanvasProvider>
  );
};

InsightsPage.propTypes = {
  hero: PropTypes.object.isRequired,
};

InsightsPage.defaultProps = {};

export default basePageWrap(InsightsPage, { noBasePageFooter: true });
